import {
  configApiRef,
  useApi,
  microsoftAuthApiRef,
} from '@backstage/core-plugin-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { WorkloadClient } from '@internal/workload-api-helper';
import { CostCenterEntry } from '../types';

export const useAllowedCostCenter = () => {
  const [allowedCostCenters, setAllowedCostCenters] = useState<
    CostCenterEntry[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const config = useApi(configApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);

  const workloadClient = useMemo(
    () => new WorkloadClient(config, oauthApi),
    [config, oauthApi],
  );

  const fetchAllowedCostCenters = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await workloadClient.apiFetch(`cost-centers`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch cost centers from cost-centers endpoint. Status: ${response.status} - ${response.statusText}. Response: ${errorText}`,
        );
      }
      const data = await response.json();
      let json_body;
      try {
        json_body = JSON.parse(data.body);
      } catch (parseError: any) {
        throw new Error(
          `Failed to parse JSON from cost-centers response: ${parseError.message}`,
        );
      }
      const cost_centers = json_body.cost_centers;

      setAllowedCostCenters(cost_centers || []); // API returns { 'cost-centers': string[] }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workloadClient]);

  useEffect(() => {
    fetchAllowedCostCenters();
  }, [fetchAllowedCostCenters]);

  return { allowedCostCenters, loading, error };
};
